import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';

export type UseFitItemsPerPageOptions = {
  /** Total items in the filtered list (used to decide whether the pager is shown). */
  itemCount: number;
  /** Vertical gap between items, in px. Defaults to 8. */
  gap?: number;
  /** Minimum items per page. Defaults to 1. */
  minPageSize?: number;
  /** Initial page size before the first successful measurement. Defaults to 1. */
  defaultPageSize?: number;
  /** Used when no item element is mounted yet. Defaults to 70. */
  fallbackItemHeight?: number;
  /** Used when the pager should show but is not mounted yet. Defaults to 28. */
  paginationFallbackHeight?: number;
};

export type UseFitItemsPerPageResult = {
  containerRef: MutableRefObject<HTMLDivElement | null>;
  itemRef: MutableRefObject<HTMLDivElement | null>;
  chromeRef: MutableRefObject<HTMLDivElement | null>;
  pagerRef: MutableRefObject<HTMLDivElement | null>;
  setContainerNode: (node: HTMLDivElement | null) => void;
  setItemNode: (node: HTMLDivElement | null) => void;
  setPagerNode: (node: HTMLDivElement | null) => void;
  pageSize: number;
  /** True after at least one measurement used a real container height. */
  hasMeasured: boolean;
  remeasure: () => void;
};

function measureBlockHeight(
  element: HTMLElement | null,
  fallback: number
): number {
  if (!element) return fallback;
  const style = window.getComputedStyle(element);
  const marginTop = parseFloat(style.marginTop) || 0;
  const marginBottom = parseFloat(style.marginBottom) || 0;
  return element.offsetHeight + marginTop + marginBottom;
}

/**
 * Dynamically calculates how many card-style items fit in a container and
 * exposes that limit for responsive pagination across screen sizes.
 */
export function useFitItemsPerPage({
  itemCount,
  gap = 8,
  minPageSize = 1,
  defaultPageSize = 1,
  fallbackItemHeight = 70,
  paginationFallbackHeight = 28,
}: UseFitItemsPerPageOptions): UseFitItemsPerPageResult {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const itemRef = useRef<HTMLDivElement | null>(null);
  const chromeRef = useRef<HTMLDivElement | null>(null);
  const pagerRef = useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const retryRafRef = useRef<number | null>(null);
  const updatePageSizeRef = useRef<(allowFallbackItem?: boolean) => boolean>(
    () => false
  );

  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [hasMeasured, setHasMeasured] = useState(false);
  const hasMeasuredRef = useRef(false);

  const updatePageSize = useCallback(
    (allowFallbackItem = false): boolean => {
      const container = containerRef.current;
      const containerHeight = container?.clientHeight ?? 0;
      if (!containerHeight) return false;

      const measuredItemHeight = itemRef.current?.offsetHeight ?? 0;
      // Wait for a real card when the list has items, unless caller allows fallback.
      if (itemCount > 0 && measuredItemHeight <= 0 && !allowFallbackItem) {
        return false;
      }

      const chromeHeight = chromeRef.current?.offsetHeight ?? 0;
      const hasChrome = chromeHeight > 0;
      const itemMarginBottom = itemRef.current
        ? parseFloat(window.getComputedStyle(itemRef.current).marginBottom) ||
          0
        : 0;
      const itemGap = itemMarginBottom > 0 ? itemMarginBottom : gap;
      const itemHeight =
        measuredItemHeight > 0 ? measuredItemHeight : fallbackItemHeight;
      const itemStride = itemHeight + itemGap;

      const chromeOffset = hasChrome ? chromeHeight + gap : 0;
      const availableHeightWithoutPager = Math.max(
        containerHeight - chromeOffset,
        itemHeight
      );
      const tentativePageSize = Math.max(
        minPageSize,
        Math.floor(availableHeightWithoutPager / itemStride)
      );

      const shouldShowPager = itemCount > tentativePageSize;
      const pagerSpace = shouldShowPager
        ? measureBlockHeight(
            pagerRef.current,
            paginationFallbackHeight + gap
          )
        : 0;
      const availableItemHeight = Math.max(
        containerHeight - chromeOffset - pagerSpace,
        itemHeight
      );
      let nextPageSize = Math.max(
        minPageSize,
        Math.floor(availableItemHeight / itemStride)
      );

      if (container && nextPageSize > 0) {
        const contentHeight =
          nextPageSize * itemStride + chromeOffset + pagerSpace;
        if (contentHeight > containerHeight && nextPageSize > minPageSize) {
          nextPageSize = Math.max(
            minPageSize,
            nextPageSize -
              Math.ceil((contentHeight - containerHeight) / itemStride)
          );
        }
      }

      if (!hasMeasuredRef.current) {
        hasMeasuredRef.current = true;
        setHasMeasured(true);
      }
      setPageSize((currentPageSize) =>
        currentPageSize === nextPageSize ? currentPageSize : nextPageSize
      );
      return true;
    },
    [
      fallbackItemHeight,
      gap,
      itemCount,
      minPageSize,
      paginationFallbackHeight,
    ]
  );

  updatePageSizeRef.current = updatePageSize;

  const clearRetry = useCallback(() => {
    if (retryRafRef.current != null && typeof window !== 'undefined') {
      window.cancelAnimationFrame(retryRafRef.current);
      retryRafRef.current = null;
    }
  }, []);

  const scheduleUpdate = useCallback(
    (allowFallbackItem = false) => {
      if (typeof window === 'undefined') return;
      clearRetry();

      let attempts = 0;
      const maxAttempts = 40;

      const tick = () => {
        retryRafRef.current = null;
        attempts += 1;
        const allow = allowFallbackItem || attempts >= 12;
        const ok = updatePageSizeRef.current(allow);
        if (!ok && attempts < maxAttempts) {
          retryRafRef.current = window.requestAnimationFrame(tick);
        }
      };

      retryRafRef.current = window.requestAnimationFrame(tick);
    },
    [clearRetry]
  );

  const observeNode = useCallback(
    (node: HTMLDivElement | null, previous: HTMLDivElement | null) => {
      const observer = resizeObserverRef.current;
      if (observer && previous && previous !== node) {
        observer.unobserve(previous);
      }
      if (observer && node) {
        observer.observe(node);
      }
      scheduleUpdate(false);
    },
    [scheduleUpdate]
  );

  const setContainerNode = useCallback(
    (node: HTMLDivElement | null) => {
      if (containerRef.current === node) return;
      const previous = containerRef.current;
      containerRef.current = node;
      observeNode(node, previous);
    },
    [observeNode]
  );

  const setItemNode = useCallback(
    (node: HTMLDivElement | null) => {
      if (itemRef.current === node) return;
      const previous = itemRef.current;
      itemRef.current = node;
      observeNode(node, previous);
    },
    [observeNode]
  );

  const setPagerNode = useCallback(
    (node: HTMLDivElement | null) => {
      if (pagerRef.current === node) return;
      const previous = pagerRef.current;
      pagerRef.current = node;
      observeNode(node, previous);
    },
    [observeNode]
  );

  const remeasure = useCallback(() => {
    scheduleUpdate(false);
    if (typeof window === 'undefined') return;
    window.setTimeout(() => scheduleUpdate(false), 0);
    window.setTimeout(() => scheduleUpdate(true), 100);
  }, [scheduleUpdate]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const onResize = () => scheduleUpdate(false);
    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(onResize);
    resizeObserverRef.current = resizeObserver;

    [
      containerRef.current,
      itemRef.current,
      chromeRef.current,
      pagerRef.current,
    ].forEach((element) => {
      if (element) resizeObserver?.observe(element);
    });

    window.addEventListener('resize', onResize);
    scheduleUpdate(false);

    return () => {
      clearRetry();
      resizeObserver?.disconnect();
      resizeObserverRef.current = null;
      window.removeEventListener('resize', onResize);
    };
  }, [clearRetry, scheduleUpdate]);

  useLayoutEffect(() => {
    scheduleUpdate(false);
  }, [itemCount, scheduleUpdate]);

  useEffect(() => () => clearRetry(), [clearRetry]);

  return {
    containerRef,
    itemRef,
    chromeRef,
    pagerRef,
    setContainerNode,
    setItemNode,
    setPagerNode,
    pageSize,
    hasMeasured,
    remeasure,
  };
}
